import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import { useGetJoinRequestListAdmin } from "../../custom-hooks/queries";
import Pagination from "../../components/CustomPagination";
import SkeletonLoader from "../../components/SkeletonLoader";
import StatusIndicator from "../../components/StatusIndicator";
import DropdownMenu from "../../components/Dropdown";
import { dateFormat } from "../../utils/helperMethods";
import { StickyActionsTd, StickyActionsTh } from "../../components/StickyActionsCells";
import {
  clickableRowClass,
  shouldIgnoreRowClick,
} from "../../utils/gridTableUx";

const PAGE_SIZE = 10;
const STATUSES = ["Pending", "Approved", "Rejected", "Cancelled"];

/**
 * ADO #14521: the backlog of sponsor requests to join an assessment. Before this
 * page the only way to reach a pending request was the link in the notification
 * email, since the sponsor chip on Manage Assessments only renders after approval.
 */
const JoinRequestsQueue = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState("Pending");
  const [currentPage, setCurrentPage] = useState(1);

  const { data, isLoading } = useGetJoinRequestListAdmin({
    current_page: currentPage,
    page_size: PAGE_SIZE,
    status,
  });

  const items = data?.items ?? [];
  const menuItems = [{ label: "View", href: "#" }];

  const handleStatusChange = (next) => {
    setStatus(next);
    setCurrentPage(1);
  };

  return (
    <div className="container w-full px-4 sm:px-6 lg:px-8 min-h-screen">
      <div className="mt-6 pb-5 space-y-3 border-b border-gray-200 px-5">
        <h1 className="font-semibold">Join Requests</h1>
        <p className="text-sm text-gray-500">
          Sponsors asking to join an existing assessment.
          {data?.total_pending > 0 &&
            ` ${data.total_pending} awaiting review.`}
        </p>
      </div>

      <div className="mt-6 px-5 flex flex-wrap gap-2">
        {STATUSES.map((option) => (
          <button
            key={option}
            type="button"
            onClick={() => handleStatusChange(option)}
            className={`rounded-md px-3 py-1.5 text-sm font-medium ${
              status === option
                ? "bg-indigo-600 text-white"
                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
            }`}
          >
            {option}
            {option === "Pending" && data?.total_pending > 0
              ? ` (${data.total_pending})`
              : ""}
          </button>
        ))}
      </div>

      <div className="mt-8 flow-root">
        <div className="-mx-4 -my-2 sm:-mx-6 lg:-mx-8">
          <div className="inline-block w-full py-2 align-middle sm:px-6 lg:px-8">
            {isLoading ? (
              <SkeletonLoader />
            ) : items.length > 0 ? (
              <div className="overflow-x-auto lg:overflow-x-visible">
                <table className="min-w-full divide-y divide-gray-300 table-fixed">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="w-1/5 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        Sponsor
                      </th>
                      <th className="w-1/5 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        Vendor
                      </th>
                      <th className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        Request Date
                      </th>
                      <th className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        Status
                      </th>
                      <th className="w-1/6 px-3 py-3.5 text-start text-sm font-semibold text-gray-900">
                        VQA Start Date
                      </th>
                      <StickyActionsTh tone="gray-100" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {items.map((item) => (
                      <tr
                        key={item.join_vqa_request_id}
                        className={clickableRowClass()}
                        onClick={(e) => {
                          if (shouldIgnoreRowClick(e.target)) {
                            return;
                          }
                          navigate(
                            `/JoinRequest/${item.join_vqa_request_id}/Details`
                          );
                        }}
                      >
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {item.sponsor_name}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {item.provider_name}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {dateFormat(item.join_vqa_request_created_on)}
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          <StatusIndicator
                            status={item.join_vqa_request_status}
                          />
                        </td>
                        <td className="px-3 py-4 text-sm text-gray-500">
                          {item.assessment_start_date
                            ? dateFormat(item.assessment_start_date)
                            : "N/A"}
                        </td>
                        <StickyActionsTd align="right">
                          <DropdownMenu
                            menuItems={menuItems}
                            navigation={`/JoinRequest/${item.join_vqa_request_id}/Details`}
                            icon={true}
                          />
                        </StickyActionsTd>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div
                className="flex flex-col items-center justify-center text-center space-y-2 min-h-96"
                style={{ marginTop: "100px" }}
              >
                <span className="p-3 rounded-full bg-custom-link-bg-color">
                  <MagnifyingGlassIcon
                    width={20}
                    height={20}
                    style={{ color: "white" }}
                  />
                </span>
                <p className="text-lg text-gray-700 font-semibold">
                  No {status} Join Requests
                </p>
                <p className="text-gray-500">
                  Nothing to review here right now.
                </p>
              </div>
            )}

            <div className="mt-5">
              <Pagination
                totalPages={data?.total_pages}
                currentPage={currentPage}
                onPageChange={setCurrentPage}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default JoinRequestsQueue;
